// components/BugReporter.jsx
// "Report a bug" header button + modal. Self-contained and config-driven so it
// can be lifted into the other SWAs: pass `authState` (whatever your app's
// useAuth exposes), an optional `contextApp`, and an optional `apiUrl`.
//
// On submit it gathers the user's description plus the captured client context
// (recent console logs, window errors, viewport, URL, auth state) from
// utils/bugLog and POSTs to /api/rprsBugReport. See that function + the
// rprs.BugReports table for the storage side.

import React, { useState, useEffect } from 'react';
import { snapshotLogs, snapshotEvents, snapshotClientContext } from '../utils/bugLog';
import KbBees, { preloadKbBees } from './KbBees';

const COLORS = {
  brandDark:  '#003D4C',
  brandGreen: '#6CC24A',
  bg:         '#ffffff',
  text:       '#1f2937',
  muted:      '#6b7280',
  border:     '#e5e7eb',
  cardBg:     '#f8fafc',
};

// One-shot cold-start retry: SWA-managed Functions can 5xx during spin-up on
// the first call of the day. Mirrors schedApi's wrapper, kept inline so this
// component carries no app-specific imports.
async function postWithRetry(url, payload) {
  const opts = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  };
  const start = Date.now();
  let r;
  try {
    r = await fetch(url, opts);
  } catch (netErr) {
    if (Date.now() - start < 5000) { await new Promise(res => setTimeout(res, 2000)); return fetch(url, opts); }
    throw netErr;
  }
  if (!r.ok && r.status >= 500 && Date.now() - start < 5000) {
    await new Promise(res => setTimeout(res, 2000));
    return fetch(url, opts);
  }
  return r;
}

export default function BugReporter({
  authState = {}, contextApp = 'rprs', apiUrl = '/api/bug-report',
  // "My reports" tab + 🐛 pulse. pulse = the user has a status change they
  // haven't seen yet; onOpen fires when the modal opens (clear the pulse /
  // mark bug_resolved notifications read). listUrl = the read proxy.
  pulse = false, onOpen, listUrl = '/api/bug-reports',
  // openSignal: bump this counter (from AppShell) to open the modal programmatically
  // — e.g. when the feature-awareness bubble is clicked. 0 on mount = no-op.
  openSignal = 0,
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('report');     // 'report' | 'mine'
  const [doing, setDoing] = useState('');
  const [happened, setHappened] = useState('');
  const [repro, setRepro] = useState('');
  const [showCaptured, setShowCaptured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null); // { bugReportId }

  // "My reports" tab state (lazy-loaded on first view per open).
  const [myReports, setMyReports] = useState(null); // null = not loaded yet
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState(null);

  // Warm the KB cheer frames the moment the modal opens, so they're cached by
  // the time the user submits — no first-play delay on the success state.
  useEffect(() => { if (open) preloadKbBees(); }, [open]);

  const reset = () => {
    setDoing(''); setHappened(''); setRepro('');
    setShowCaptured(false); setBusy(false); setError(null); setDone(null);
  };
  const close = () => { setOpen(false); reset(); setTab('report'); setMyReports(null); };

  // Open: if there's an unseen status change, land on "My reports"; either way
  // tell the parent (clears the pulse / marks the resolved notifications read).
  const openModal = () => {
    setOpen(true);
    setTab(pulse ? 'mine' : 'report');
    try { onOpen?.(); } catch (_) { /* ignore */ }
  };

  // Open programmatically when AppShell bumps openSignal (awareness bubble click).
  // openSignal starts at 0 → the mount run is a no-op; only a later bump opens.
  useEffect(() => { if (openSignal) openModal(); }, [openSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMyReports = async () => {
    setMyLoading(true); setMyError(null);
    try {
      const r = await postWithRetry(listUrl, { scope: 'mine' });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`(${r.status}) ${t}`); }
      const data = await r.json();
      setMyReports(Array.isArray(data.reports) ? data.reports : []);
    } catch (e) {
      setMyError(e?.message || String(e));
      setMyReports([]);
    } finally {
      setMyLoading(false);
    }
  };

  // Lazy-load my reports the first time the tab is shown while open.
  useEffect(() => {
    if (open && tab === 'mine' && myReports === null && !myLoading) loadMyReports();
  }, [open, tab, myReports, myLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = doing.trim().length > 0 || happened.trim().length > 0;

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        contextApp,
        title: doing.trim().slice(0, 120),
        description: { doing: doing.trim(), happened: happened.trim(), repro: repro.trim() },
        context: {
          ...snapshotClientContext(),
          authState: {
            email: authState.email || null,
            username: authState.username || null,
            displayName: authState.displayName || null,
            isSuperUser: !!authState.isSuperUser,
          },
        },
        logs: snapshotLogs(),
        events: snapshotEvents(),
      };
      const r = await postWithRetry(apiUrl, payload);
      if (!r.ok) {
        const txt = await r.text().catch(() => '');
        throw new Error(`Submit failed (${r.status}): ${txt}`);
      }
      const data = await r.json();
      setDone({ bugReportId: data.bugReportId });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const logCount = snapshotLogs().length;
  const eventCount = snapshotEvents().length;

  return (
    <>
      <button
        className={`app-bugreport-btn${pulse ? ' app-bugreport-btn--pulse' : ''}`}
        title={pulse ? 'A bug you reported was updated' : 'Report a bug'}
        aria-label="Report a bug"
        onClick={openModal}
      >
        &#128027;{/* 🐛 */}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) close(); }}
        >
          <div style={{
            background: COLORS.bg, color: COLORS.text, borderRadius: 14,
            width: 'min(580px, 94vw)', maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,.35)',
          }}>
            {/* Header */}
            <div style={{
              background: COLORS.brandDark, color: 'white', padding: '18px 24px',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 26, lineHeight: 1 }}>&#128027;</span>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.1 }}>Report a bug</div>
                <div style={{ fontSize: 13, color: '#cbe5d8', marginTop: 2 }}>
                  Tell us what went wrong — we'll attach the technical details automatically.
                </div>
              </div>
            </div>

            {/* Tab strip */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, background: '#fff' }}>
              <TabBtn active={tab === 'report'} onClick={() => setTab('report')}>Report a bug</TabBtn>
              <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')}>My reports</TabBtn>
            </div>

            {tab === 'mine' ? (
              <MyReportsPanel
                reports={myReports} loading={myLoading} error={myError}
                onReport={() => setTab('report')}
              />
            ) : done ? (
              // Success state
              <div style={{ padding: '28px 24px', textAlign: 'center' }}>
                <KbBees size={132} fps={8} />
                <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.brandDark }}>Thanks — report sent.</div>
                <p style={{ fontSize: 13.5, color: COLORS.muted, margin: '8px 0 0', lineHeight: 1.5 }}>
                  Reference <strong>#{done.bugReportId}</strong>. The team can now see what you were doing,
                  the page you were on, and recent app activity.
                </p>
                <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button onClick={close} style={primaryBtn}>Close</button>
                  <button onClick={reset} style={secondaryBtn}>Report another</button>
                </div>
              </div>
            ) : (
              <>
                {/* Body */}
                <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14 }}>
                  <Field label="What were you trying to do?" required>
                    <textarea
                      value={doing} onChange={(e) => setDoing(e.target.value)}
                      placeholder="e.g. Move a job to next week on the Scheduler grid"
                      rows={2} style={textareaStyle} autoFocus
                    />
                  </Field>

                  <Field label="What happened instead?">
                    <textarea
                      value={happened} onChange={(e) => setHappened(e.target.value)}
                      placeholder="e.g. The job snapped back and a red error flashed"
                      rows={2} style={textareaStyle}
                    />
                  </Field>

                  <Field label="Steps to reproduce (optional)">
                    <textarea
                      value={repro} onChange={(e) => setRepro(e.target.value)}
                      placeholder="1. … 2. … 3. …"
                      rows={2} style={textareaStyle}
                    />
                  </Field>

                  {/* Captured-context disclosure */}
                  <button
                    type="button"
                    onClick={() => setShowCaptured(s => !s)}
                    style={{
                      background: 'none', border: 'none', padding: 0, marginTop: 4,
                      color: COLORS.brandDark, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {showCaptured ? '▾' : '▸'} We'll also include page &amp; technical details ({logCount} log lines, {eventCount} events)
                  </button>
                  {showCaptured && (
                    <ul style={{
                      margin: '8px 0 0', padding: '10px 10px 10px 26px', fontSize: 12.5,
                      color: COLORS.muted, background: COLORS.cardBg, borderRadius: 8, lineHeight: 1.6,
                    }}>
                      <li>Current page URL &amp; route</li>
                      <li>Your sign-in (name &amp; email) and role</li>
                      <li>Browser, screen &amp; viewport size</li>
                      <li>Recent console logs and any errors captured this session</li>
                    </ul>
                  )}

                  {error && (
                    <div style={{ marginTop: 14, background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 13 }}>
                      {error}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{
                  padding: '14px 24px 18px', borderTop: `1px solid ${COLORS.border}`,
                  display: 'flex', gap: 10, justifyContent: 'flex-end',
                }}>
                  <button onClick={close} disabled={busy} style={secondaryBtn}>Cancel</button>
                  <button onClick={submit} disabled={!canSubmit || busy}
                    style={{ ...primaryBtn, opacity: (!canSubmit || busy) ? 0.55 : 1, cursor: (!canSubmit || busy) ? 'not-allowed' : 'pointer' }}>
                    {busy ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        flex: 1, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 13, fontWeight: 700, color: active ? COLORS.brandDark : COLORS.muted,
        borderBottom: active ? `2px solid ${COLORS.brandGreen}` : '2px solid transparent',
      }}>
      {children}
    </button>
  );
}

const STATUS_LABELS = {
  open:        { t: 'Open',        c: '#92400E', b: '#FEF3C7' },
  triaged:     { t: 'Triaged',     c: '#1D4ED8', b: '#DBEAFE' },
  in_progress: { t: 'In progress', c: '#3730A3', b: '#E0E7FF' },
  resolved:    { t: 'Resolved',    c: '#065F46', b: '#D1FAE5' },
  wont_fix:    { t: "Won't fix",   c: '#6B7280', b: '#F3F4F6' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}

// Read-only list of the signed-in user's own reports + their current status/note.
function MyReportsPanel({ reports, loading, error, onReport }) {
  return (
    <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14, minHeight: 160 }}>
      {loading ? (
        <div style={{ textAlign: 'center', color: COLORS.muted, padding: 24, fontSize: 13 }}>Loading your reports…</div>
      ) : error ? (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 10, borderRadius: 6, fontSize: 13 }}>
          Couldn't load your reports: {error}
        </div>
      ) : !reports || reports.length === 0 ? (
        <div style={{ textAlign: 'center', color: COLORS.muted, padding: '28px 12px' }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>&#128027;</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>You haven't reported any bugs yet.</div>
          <button onClick={onReport} style={{ ...secondaryBtn, marginTop: 14 }}>Report a bug</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map(r => {
            const s = STATUS_LABELS[r.status] || STATUS_LABELS.open;
            return (
              <div key={r.bugReportId} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 12px', background: COLORS.cardBg }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 13.5, lineHeight: 1.3 }}>
                    {r.title || '(no title)'}
                  </div>
                  <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: s.c, background: s.b, whiteSpace: 'nowrap' }}>
                    {s.t}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 3 }}>
                  #{r.bugReportId} · {fmtDate(r.createdAt)}
                </div>
                {r.resolutionNote && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: COLORS.brandDark }}>Update from the team: </span>
                    {r.resolutionNote}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const textareaStyle = {
  width: '100%', boxSizing: 'border-box', resize: 'vertical',
  border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px',
  fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.45, color: '#1f2937',
};
const primaryBtn = {
  padding: '10px 22px', background: COLORS.brandGreen, color: 'white',
  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const secondaryBtn = {
  padding: '10px 18px', background: 'white', color: COLORS.muted,
  border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
